import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { updateUrlSearchParams } from '@utils/search';
import logger from '@utils/logs';
import { AppliedFilterValue } from '@context';

interface UseFilterBuilderItemProps {
    paramName: string;
    data: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        data: any[];
    } | null;
    // request data might not be necessary
    requestData: (() => Promise<void>) | null;
    appliedValues: string[];
    setFilterValues: (values: AppliedFilterValue[]) => void;
    labelField: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useFilterBuilder: (filters: UseFilterBuilderItemProps[]) => { getOptions: any; toggleValue: any, setOnlyValue: any } = (filters) => {
    const location = useLocation();
    const navigate = useNavigate();

    const queryParams = new URLSearchParams(location.search);

    // example:
    // {
    //     'color': [1, 2, 7, 5],
    //     'size': [6, 3, 9],
    // }
    // initial values are the ones defined in url GET params
    const [search, setSearch] = useState<Record<string, string[]>>(() =>
        filters.reduce((acc, filter) => {
            const getFilterParam = queryParams.get(filter.paramName);
            return { ...acc, [filter.paramName]: getFilterParam?.split(',') || [] };
        }, {})
    );

    // if applied values change, refresh
    // this can be triggered when the filter is removed on AppliedFilters
    useEffect(() => {
        logger.log('hooks > useFilterBuilder > appliedValues changed');
        filters.forEach((filter) => {
            if (filter.appliedValues.length === 0 && search[filter.paramName]?.length > 0) {
                setSearch((previous) => {
                    const otherFilters = { ...previous };
                    delete otherFilters[filter.paramName];
                    return otherFilters;
                });
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.map((filter) => filter.appliedValues).toString()]);

    // call service, if we don't have the information yet
    useEffect(() => {
        logger.log('hooks > useFilterBuilder > data changed');
        filters.forEach((filter) => {
            !filter.data && filter.requestData && filter.requestData();

            const getFilterParam = queryParams.get(filter.paramName);

            setSearch((previous) => {
                const otherFilters = { ...previous };
                delete otherFilters[filter.paramName];
                return { ...otherFilters, [filter.paramName]: getFilterParam?.split(',') || [] };
            });
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.map((filter) => filter.data === null).toString()]);

    // initially, search gets values from the url search params
    useEffect(() => {
        logger.log('hooks > useFilterBuilder > location.search changed', location.search);
        filters.forEach((filter) => {
            const getFilterParam = queryParams.get(filter.paramName);

            if (search[filter.paramName]?.join(',') !== getFilterParam) {
                setSearch((previous) => {
                    const otherFilters = { ...previous };
                    delete otherFilters[filter.paramName];
                    return { ...otherFilters, [filter.paramName]: getFilterParam?.split(',') || [] };
                });
            }
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    // update url when values being searched change
    useEffect(() => {
        logger.log('hooks > useFilterBuilder > search changed', { search });

        filters.forEach((filter) => {
            const getFilterParam = queryParams.get(filter.paramName);

            search[filter.paramName]?.join(',') !== getFilterParam && updateUrl(filter.paramName);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    // update applied filters (if they exist)
    useEffect(() => {
        logger.log('hooks > useFilterBuilder > data and search changed', { search });
        filters.forEach((filter) => {
            if (filter.data !== null) {
                filter.setFilterValues(
                    filter.data?.data
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        ?.filter((item: any) => search[filter.paramName]?.includes(`${item.uuid || ''}`))
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        .map((item: any) => {
                            return { uuid: item.uuid, name: item[filter.labelField] || '' };
                        })
                );
            }
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.map((filter) => filter.data === null).toString(), search]);

    const updateUrl = (paramName: string) => {
        const updatedUrl = updateUrlSearchParams({ [paramName]: search[paramName] || [] });
        logger.log('hooks > useFilterBuilder > updateUrl', { paramName, updatedUrl });
        navigate(updatedUrl, { replace: true });
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const getOptions: (paramName: string) => { id: string; value: string; label: string; checked: boolean }[] = (paramName) => {
        const filter = filters.find((item) => item.paramName === paramName);

        logger.log('hooks > useFilterBuilder > getOptions', { paramName, filter });

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (
            filter?.data?.data?.map((item) => {
                return {
                    ...item,
                    id: `${item.uuid}`,
                    value: `${item.uuid}`,
                    label: item[filter.labelField],
                    checked: search[paramName]?.includes(`${item.uuid}`) || false,
                };
            }) || []
        );
    };

    const toggleValue = (paramName: string, valueId: string) => {
        setSearch((previous) => {
            const currentFilter = previous[paramName]?.includes(valueId)
                ? // remove
                  previous[paramName].filter((value) => value !== valueId)
                : // add
                  [...(previous[paramName] || []), valueId];

            if (currentFilter.length > 0) {
                logger.log('hooks > useFilterBuilder > toggleValue > adding/editing filter to search', { paramName, valueId });

                return { ...search, [paramName]: currentFilter };
            } else {
                logger.log('hooks > useFilterBuilder > toggleValue > removing filter from search', { paramName, valueId });

                const otherFilters = { ...search };
                delete otherFilters[paramName];
                return { ...otherFilters };
            }
        });
    };

    const setOnlyValue = (paramName: string, valueId: string) => {
        setSearch((previous) => {
            const alreadySelected = previous[paramName]?.includes(valueId);

            if (alreadySelected) {
                return previous;
            }

            logger.log('hooks > useFilterBuilder > setOnlyValue > setting only this filter', { paramName, valueId });

            return { ...previous, [paramName]: [valueId] };
        });
    };

    return { getOptions, toggleValue, setOnlyValue };
};
